import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AccountType } from 'src/app/shared/constants/account';
import { APIService } from 'src/app/shared/services/api.service';
import { environment } from 'src/environments/environment';

import urlJoin from 'url-join';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private baseClientUrl = urlJoin(environment.API_URL, 'account/');

  constructor(private http: HttpClient, private apiService: APIService) { }

  public async getAccountList(params = {}) {
    const url = this.apiService.url('account/');
    return this.http.get(url, { params }).toPromise();
  }

  public async getAdvertiserAccountList(params = {}) {
    params = { ...params, account_type: AccountType.ADVERTISER };
    return await this.getAccountList(params);
  }

  public async getPublisherAccountList(params = {}) {
    params = { ...params, account_type: AccountType.PUBLISHER };
    return await this.getAccountList(params);
  }

  public async getAdminAccountList(params = {}) {
    return await this.getAccountList({
      ...params,
      account_type: AccountType.ADMIN,
    });
  }

  public getPublisherAccount(id: number) {
    const url = urlJoin(this.baseClientUrl, `${id}/publisher_settings/`);
    return this.http.get(url);
  }

  public getAdvertiserAccount(id: number) {
    const url = urlJoin(this.baseClientUrl, `${id}/advertiser_settings/`);
    return this.http.get(url);
  }

  public getAccountInfo(id: number) {
    const url = urlJoin(this.baseClientUrl, `${id}/`);
    return this.http.get(url);
  }

  public getAccount(id: number, type: string) {
    switch (type) {
      case AccountType.ADVERTISER:
        return this.getAdvertiserAccount(id);

      case AccountType.PUBLISHER:
        return this.getPublisherAccount(id);

      default:
        return null;
    }
  }

  public updatePublisherAccount(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `${id}/publisher_settings/`);
    return this.http.patch(url, body);
  }

  public updateAdvertiserAccount(id: number, body) {
    const url = urlJoin(this.baseClientUrl, `${id}/advertiser_settings/`);
    return this.http.patch(url, body);
  }

  public updateAccount(id: number, type: string, body: any) {
    switch (type) {
      case AccountType.ADVERTISER:
        return this.updateAdvertiserAccount(id, body);

      case AccountType.PUBLISHER:
        return this.updatePublisherAccount(id, body);

      default:
        return null;
    }
  }

  public async getAllAccounts(filters = {}) {
    const url = this.apiService.url('account/');
    return this.http.get(url, { params: filters }).toPromise();
  }

  public async getAllAdvertiserAccounts() {
    return await this.getAllAccounts({
      account_type: AccountType.ADVERTISER,
    });
  }

  public async getAllPublisherAccounts() {
    return await this.getAllAccounts({
      account_type: AccountType.PUBLISHER,
    });
  }

  public async getIsEventProcessingSlow() {
    return this.http.get(this.apiService.url('event-processing/slow/')).toPromise();
  }

  public async sendNewCampaignStartedNotification(adSpaces: number[], offerId: number) {
    const url = this.apiService.url('notification/new_campaign_started/');
    const body = {
      ad_spaces: adSpaces,
      offer: offerId,
    };
    return this.http.post(url, body).toPromise();
  }

  public async sendAdSpaceAcceptedNotification(adSpaces: number[], offerId: number) {
    const url = this.apiService.url('notification/adspace_accepted/');
    const body = {
      ad_spaces: adSpaces,
      offer: offerId,
    };
    return this.http.post(url, body).toPromise();
  }

  public async sendCampaignPausedNotification(adSpaces: number[], offerId: number) {
    const url = this.apiService.url('notification/campaign_paused/');
    const body = {
      ad_spaces: adSpaces,
      offer: offerId,
    };
    return this.http.post(url, body).toPromise();
  }

  public async sendCampaignReactivatedNotification(adSpaces: number[], offerId: number) {
    const url = this.apiService.url('notification/campaign_reactivated/');
    const body = {
      ad_spaces: adSpaces,
      offer: offerId,
    };
    return this.http.post(url, body).toPromise();
  }

  public sendAdNotification(notificationType: string, selection: number[] = [], offerId: number) {
    const url = this.apiService.url(`notification/${notificationType}/`);
    const body = {
      selection,
      offer: offerId,
    };
    return this.http.post(url, body).toPromise();
  }

  public async getAccountUploads(account: number) {
    return this.http.get(this.apiService.url('account-uploads/') + `?account=${account}`).toPromise();
  }

  public async createAccountUpload(body: any) {
    const url = this.apiService.url('account-uploads/');
    return this.http.post(url, body).toPromise();
  }

  public async deleteAccountUpload(id: number) {
    return this.http.delete(this.apiService.url(`account-uploads/${id}/`)).toPromise();
  }
}
